package ai.jessie.web.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.rememberUpdatedState
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

/**
 * Right-aligned slide-over (`docs/design/components.md`, SlideOver): scrim, panel with the dialog
 * semantics, head with the caller's [heading] and a ghost close button, scrolling [body] and a
 * footer holding the [actions]. [labelledBy] names the heading element inside [heading]; the caller
 * owns that element and its focus. Escape and the scrim call [close].
 */
@Composable
fun slideOver(
    panelId: String,
    labelledBy: String,
    close: () -> Unit,
    busy: Boolean = false,
    heading: @Composable () -> Unit,
    actions: @Composable () -> Unit,
    body: @Composable () -> Unit,
) {
    Div(
        attrs =
            ScrimStyle.toAttrs {
                onClick { close() }
                attr("aria-hidden", "true")
            }
    )
    Div(
        attrs =
            SlideOverStyle.toAttrs {
                id(panelId)
                attr("role", "dialog")
                attr("aria-modal", "true")
                attr("aria-labelledby", labelledBy)
                attr("tabindex", "-1")
            }
    ) {
        Div(attrs = SlideOverHeadStyle.toAttrs()) {
            Div { heading() }
            Button(
                attrs =
                    ButtonStyle.toModifier(GhostButtonVariant)
                        .then(IconButtonStyle.toModifier())
                        .toAttrs {
                            attr("type", "button")
                            attr("aria-label", "Schliessen")
                            if (busy) attr("disabled", "")
                            onClick { close() }
                        }
            ) {
                icon("times")
            }
        }
        Div(attrs = SlideOverBodyStyle.toAttrs()) { body() }
        Div(attrs = SlideOverFootStyle.toAttrs()) {
            Div(attrs = SlideOverActionsStyle.toAttrs()) { actions() }
        }
    }
    slideOverKeyboard(panelId, close)
}

/** A native submit button for the form [formId], e.g. in a slide-over footer outside the form. */
@Composable
fun formSubmitButton(
    label: String,
    formId: String,
    disabled: Boolean = false,
    variant: ButtonVariant = ButtonVariant.PRIMARY,
) {
    Button(
        attrs =
            ButtonStyle.toModifier(variant.toSilk()).toAttrs {
                attr("type", "submit")
                attr("form", formId)
                if (disabled) attr("disabled", "")
            }
    ) {
        Text(label)
    }
}

/** Keep the surrounding application inert while retaining its DOM for focus return. */
@Composable
fun slideOverKeyboard(id: String, close: () -> Unit) {
    val latestClose = rememberUpdatedState(close)
    DisposableEffect(id) {
        val panel = document.getElementById(id) as HTMLElement
        val body = checkNotNull(document.body)
        val overflow = body.style.getPropertyValue("overflow")
        body.style.setProperty("overflow", "hidden")
        val inert = inertBackground(panel)
        val keydown: (Event) -> Unit = { event ->
            val key = event as KeyboardEvent
            when (key.key) {
                "Escape" -> {
                    key.preventDefault()
                    latestClose.value()
                }
                "Tab" -> trapTab(panel, key)
            }
        }
        document.addEventListener("keydown", keydown)
        panel.focus()
        onDispose {
            body.style.setProperty("overflow", overflow)
            document.removeEventListener("keydown", keydown)
            inert.forEach { it.removeAttribute("inert") }
        }
    }
}

private fun inertBackground(panel: HTMLElement): List<HTMLElement> {
    val inert = mutableListOf<HTMLElement>()
    var ancestor: HTMLElement? = panel
    while (ancestor != null && ancestor != document.body) {
        val parent = ancestor.parentElement ?: break
        val siblings = parent.children
        for (index in 0 until siblings.length) {
            val sibling = siblings.item(index) as? HTMLElement ?: continue
            if (
                sibling != ancestor &&
                    !sibling.hasAttribute("inert") &&
                    sibling.getAttribute("aria-hidden") != "true"
            ) {
                sibling.setAttribute("inert", "")
                inert += sibling
            }
        }
        ancestor = parent as? HTMLElement
    }
    return inert
}

private fun trapTab(panel: HTMLElement, key: KeyboardEvent) {
    val nodes = panel.querySelectorAll("a[href], button, input, select, textarea, [tabindex='0']")
    val controls =
        (0 until nodes.length)
            .map { nodes.item(it) as HTMLElement }
            .filter {
                !it.matches(":disabled") &&
                    it.getAttribute("aria-disabled") != "true" &&
                    it.getClientRects().length > 0
            }
    val index = controls.indexOf(document.activeElement)
    val target =
        when {
            controls.isEmpty() -> panel
            key.shiftKey && index <= 0 -> controls.last()
            !key.shiftKey && (index == -1 || index == controls.lastIndex) -> controls.first()
            else -> null
        }
    target?.let {
        key.preventDefault()
        it.focus()
    }
}

/** Scrim behind a slide-over. */
val ScrimStyle = CssStyle.base {
    css(
        "position" to "fixed",
        "inset" to "0",
        "background" to OverlayVar.value(),
        "z-index" to "20",
    )
}

val SlideOverStyle = CssStyle.base {
    css(
        "position" to "fixed",
        "top" to "0",
        "right" to "0",
        "bottom" to "0",
        "width" to "min(${JessieTokens.SLIDE_OVER_WIDTH}, 100%)",
        "background" to SurfaceVar.value(),
        "box-shadow" to JessieTokens.SHADOW_SLIDE_OVER,
        "display" to "flex",
        "flex-direction" to "column",
        "z-index" to "21",
    )
}

val SlideOverHeadStyle = CssStyle {
    base {
        css(
            "display" to "flex",
            "align-items" to "flex-start",
            "justify-content" to "space-between",
            "padding" to "24px 28px 18px",
            "border-bottom" to "1px solid ${LineVar.value()}",
        )
    }
    // Slide-over title 20/700 (components.md), one step bolder than the base h2.
    cssRule(" h2") { css("font-weight" to "700") }
}

val SlideOverBodyStyle = CssStyle.base {
    css(
        "padding" to "20px 28px",
        "display" to "flex",
        "flex-direction" to "column",
        "gap" to "18px",
        "flex-grow" to "1",
        "overflow" to "auto",
    )
}

val SlideOverFootStyle = CssStyle.base {
    css(
        "display" to "flex",
        "align-items" to "center",
        "justify-content" to "space-between",
        "gap" to "8px",
        "padding" to "16px 28px",
        "border-top" to "1px solid ${LineVar.value()}",
        "background" to SurfaceMutedVar.value(),
    )
}

// Actions wrap onto a second line instead of being clipped by the panel.
val SlideOverActionsStyle = CssStyle.base {
    css(
        "display" to "flex",
        "flex-wrap" to "wrap",
        "justify-content" to "flex-end",
        "gap" to "8px",
        "margin-left" to "auto",
    )
}
